import { useInvoiceAlertState } from '@features/home/hooks/use-invoice-alert-state/useInvoiceAlertState';
import { useMFEAlertPanelState } from '@features/home/hooks/use-mfe-alert-panel-state/useMFEAlertPanelState';
import * as React from 'react';

import type {
  AlertError,
  ReservationAlertType,
} from './useDashboardAlertsState.mfe.types';
import type { UseDashboardAlertsStateReturn } from './useDashboardAlertsState.types';

export function useDashboardAlertsState(options?: {
  enabled?: boolean;
}): UseDashboardAlertsStateReturn {
  const invoice = useInvoiceAlertState({ enabled: options?.enabled ?? true });
  const mfe = useMFEAlertPanelState();
  const {
    closeMFEPanel,
    openMFEPanel: openMFEAlertsPanel,
    setMFEAlertState,
  } = mfe;
  const {
    closePanel,
    handleDetailClose,
    handleInvoiceClick,
    hasInvoiceAlerts,
    invoiceError,
    invoiceIsLoading,
    isPanelOpen,
    openPanel: openInvoicePanel,
    pastDueCount,
    pastDueError,
    pastDueInvoices,
    pastDueIsLoading,
    selectedInvoiceId,
  } = invoice;

  const isLoading = invoiceIsLoading || mfe.mfeAlertState.isLoading;

  const alertError = React.useMemo((): AlertError => {
    let errorType: 'invoices' | 'mfe' | 'both' | null = null;

    if (invoiceError && mfe.mfeAlertState.isError) {
      errorType = 'both';
    } else if (invoiceError) {
      errorType = 'invoices';
    } else if (mfe.mfeAlertState.isError) {
      errorType = 'mfe';
    }

    return {
      type: errorType,
      invoiceError,
      mfeError: mfe.mfeAlertState.isError
        ? new Error('Failed to load reservation alerts')
        : null,
    };
  }, [invoiceError, mfe.mfeAlertState.isError]);

  const hasMFEAlerts =
    !mfe.mfeAlertState.isError &&
    mfe.mfeAlertState.alerts.some(a => a.count > 0);

  const showAlertsSection = (hasInvoiceAlerts && !invoiceError) || hasMFEAlerts;

  const activePanel = React.useMemo(() => {
    if (selectedInvoiceId) {
      return 'invoice-detail';
    }

    if (mfe.isMFEPanelOpen) {
      return 'mfe';
    }

    if (isPanelOpen) {
      return 'invoice-list';
    }

    return null;
  }, [selectedInvoiceId, mfe.isMFEPanelOpen, isPanelOpen]);

  const openPanel = React.useCallback((): void => {
    closeMFEPanel();
    openInvoicePanel();
  }, [closeMFEPanel, openInvoicePanel]);

  const openMFEPanel = React.useCallback(
    (alertType: ReservationAlertType): void => {
      closePanel();
      openMFEAlertsPanel(alertType);
    },
    [closePanel, openMFEAlertsPanel]
  );

  return {
    // Invoice alerts
    hasInvoiceAlerts,
    pastDueCount,
    pastDueInvoices,
    pastDueIsLoading,
    pastDueError,
    invoiceIsLoading,
    invoiceError,

    // MFE alerts
    mfeAlertState: mfe.mfeAlertState,
    setMFEAlertState,
    hasMFEAlerts,

    // Combined state
    isLoading,
    alertError,
    showAlertsSection,

    // Invoice panel
    isPanelOpen,
    selectedInvoiceId,
    activePanel,
    openPanel,
    closePanel,
    handleInvoiceClick,
    handleDetailClose,

    // MFE panel
    selectedMFEAlertType: mfe.selectedMFEAlertType,
    isMFEPanelOpen: mfe.isMFEPanelOpen,
    openMFEPanel,
    closeMFEPanel: mfe.closeMFEPanel,
  };
}
